import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Alert, Button, ConfirmDialog, Panel, Spinner, inputClass } from '../components/ui';
import { NAME_MAX, type Project, type ProjectSummary } from '../domain/schemas';
import { downloadText } from '../lib/download';
import { GENRES } from '../music/generator/genres';
import type { Route } from '../state/route';
import { useServices } from '../state/services';
import { describeBackup, downloadUnsavedCopy } from '../state/recovery';
import { useObservable } from '../lib/useObservable';
import { StorageError, toStorageError } from '../storage/errors';
import { withProjectLock } from '../state/projectLock';
import { MAX_IMPORT_BYTES, exportProject, importProject } from '../storage/importExport';
import { persistenceStatus, requestPersistentStorage, type PersistenceStatus } from '../lib/persistentStorage';
import type { CorruptedRecord } from '../storage/projectRepository';

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; error: StorageError }
  | { status: 'ready'; projects: ProjectSummary[]; corrupted: CorruptedRecord[] };

type Notice = { tone: 'error' | 'warning' | 'success'; title: string; details?: string[] } | null;

/**
 * Renames and deletes run while holding the project's editing lock, so another tab can't
 * open the project in between. A project open elsewhere, or a lock state that cannot be
 * checked, refuses the action: a failed check is never taken as "not open anywhere".
 */
async function whileNotOpenElsewhere(projectId: string, action: () => Promise<void>) {
  const result = await withProjectLock(projectId, action);
  if (result.kind === 'held-elsewhere') {
    throw new StorageError('conflict', 'This project is open in another tab or window.', ['Close it there first, then try again.']);
  }
  if (result.kind === 'failed') {
    throw new StorageError('lock-failed', 'SonicForge could not check whether this project is open in another tab, so nothing was changed.', [
      'Close other SonicForge tabs and try again; if it keeps failing, reload the page.',
    ]);
  }
}

const dateFormat = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });

export function Dashboard({ navigate }: { navigate: (route: Route) => void }) {
  const { repo, recovery } = useServices();
  const unsaved = useObservable(recovery);
  const [persistence, setPersistence] = useState<PersistenceStatus>('unknown');
  useEffect(() => {
    // Reads the browser's storage persistence (external system), asynchronously.
    void persistenceStatus().then(setPersistence);
  }, []);
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ProjectSummary | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    try {
      const listing = await repo.list();
      setState({ status: 'ready', ...listing });
    } catch (error) {
      setState({ status: 'error', error: toStorageError(error, 'Could not read your projects.') });
    }
  }, [repo]);

  useEffect(() => {
    // Initial load from IndexedDB (external system); state is set asynchronously.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh();
  }, [refresh]);

  const run = async (action: () => Promise<void>, failure: string) => {
    setBusy(true);
    setNotice(null);
    try {
      await action();
    } catch (error) {
      const e = toStorageError(error, failure);
      setNotice({ tone: 'error', title: e.message, details: e.details });
    } finally {
      setBusy(false);
      await refresh();
    }
  };

  /** Downloads the unsaved work as an importable backup and says exactly what it contains. */
  const backupUnsaved = async (project: Project) => {
    setBusy(true);
    setNotice(null);
    try {
      const summary = await downloadUnsavedCopy(repo, project);
      setNotice(describeBackup(summary));
    } catch (error) {
      const e = toStorageError(error, 'The backup could not be created.');
      setNotice({ tone: 'error', title: `No backup was downloaded: ${e.message}`, details: e.details });
    } finally {
      setBusy(false);
    }
  };

  const onCreate = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError('Give your project a name.');
      return;
    }
    setNameError(null);
    void run(async () => {
      const project = await repo.create(trimmed);
      void requestPersistentStorage().then(setPersistence);
      setName('');
      navigate({ name: 'project', projectId: project.id, tab: 'music' });
    }, 'Could not create the project.');
  };

  const onImport = async (file: File | undefined) => {
    if (!file) return;
    await run(async () => {
      // Refuse oversized files before reading them into memory.
      if (file.size > MAX_IMPORT_BYTES * 1.4) throw new StorageError('invalid-file', 'The file is too large to import.', [`${(file.size / 1048576).toFixed(0)} MB; the limit is ${MAX_IMPORT_BYTES / 1048576} MB.`]);
      const text = await file.text();
      const project = await importProject(repo, text);
      void requestPersistentStorage().then(setPersistence);
      setNotice({ tone: 'success', title: `Imported “${project.name}”.` });
    }, 'Import failed.');
    if (fileInput.current) fileInput.current.value = '';
  };

  const onExport = (id: string) =>
    run(async () => {
      const { fileName, text } = await exportProject(repo, id);
      downloadText(fileName, text);
    }, 'Export failed.');

  const onRename = (event: FormEvent) => {
    event.preventDefault();
    if (!renaming) return;
    const target = renaming;
    setRenaming(null);
    void run(async () => {
      await whileNotOpenElsewhere(target.id, async () => {
        await repo.rename(target.id, target.name);
      });
    }, 'Rename failed.');
  };

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-6 sm:px-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Projects</h1>
        <p className="mt-1 text-sm text-zinc-400">
          Everything is stored privately in this browser (IndexedDB). Export projects to back them up.
        </p>
        <p className="mt-1 text-xs text-zinc-400" data-testid="storage-persistence" data-status={persistence}>
          {persistence === 'persistent' && 'This browser has agreed to keep SonicForge’s data (persistent storage).'}
          {persistence === 'best-effort' &&
            'This browser may clear SonicForge’s data when space runs low or after a long time without a visit. Keep exported backups of projects you care about.'}
          {persistence === 'unknown' && 'Browsers can clear site data under storage pressure. Keep exported backups of projects you care about.'}
        </p>
      </div>

      <Panel title="New project">
        <form onSubmit={onCreate} className="flex flex-col gap-3 sm:flex-row sm:items-start" noValidate>
          <div className="flex-1">
            <label htmlFor="new-project-name" className="sr-only">
              Project name
            </label>
            <input
              id="new-project-name"
              className={inputClass}
              placeholder="Project name, e.g. Midnight Drive"
              value={name}
              maxLength={NAME_MAX}
              aria-invalid={nameError ? true : undefined}
              aria-describedby={nameError ? 'new-project-error' : undefined}
              onChange={(e) => setName(e.target.value)}
            />
            {nameError && (
              <p id="new-project-error" className="mt-1 text-xs text-red-400">
                {nameError}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="submit" variant="primary" disabled={busy}>
              Create project
            </Button>
            <Button onClick={() => fileInput.current?.click()} disabled={busy}>
              Import…
            </Button>
            <input
              ref={fileInput}
              type="file"
              accept=".json,application/json"
              className="sr-only"
              tabIndex={-1}
              aria-label="Import project file"
              data-testid="import-input"
              onChange={(e) => void onImport(e.target.files?.[0])}
            />
          </div>
        </form>
      </Panel>

      {unsaved && (
        <div data-testid="unsaved-recovery">
          <Alert
            title={`Changes to “${unsaved.project.name}” could not be saved when it was closed.`}
            action={
              <div className="flex gap-2">
                <Button size="sm" onClick={() => void backupUnsaved(unsaved.project)} disabled={busy} data-testid="unsaved-download">
                  Download them as a backup
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmDiscard(true)} data-testid="unsaved-dismiss">
                  Discard
                </Button>
              </div>
            }
          >
            {unsaved.error} The changes are kept in this tab until you discard them or close the page. Download the backup, then import it below.
          </Alert>
        </div>
      )}
      {notice && (
        <Alert
          tone={notice.tone}
          title={notice.title}
          action={
            <Button size="sm" variant="ghost" onClick={() => setNotice(null)} aria-label="Dismiss message">
              Dismiss
            </Button>
          }
        >
          {notice.details && notice.details.length > 0 && (
            <ul className="list-disc pl-5 font-mono text-xs">
              {notice.details.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
          )}
        </Alert>
      )}

      {state.status === 'loading' && <Spinner label="Loading projects…" />}
      {state.status === 'error' && (
        <Alert title={state.error.message} action={<Button size="sm" onClick={() => void refresh()}>Retry</Button>} />
      )}
      {state.status === 'ready' && (
        <>
          {state.corrupted.length > 0 && (
            <Alert tone="warning" title={`${state.corrupted.length} stored project(s) could not be read`}>
              They were left untouched. IDs: {state.corrupted.map((c) => c.id).join(', ')}
            </Alert>
          )}
          {state.projects.length === 0 ? (
            <div className="rounded-xl border border-dashed border-zinc-700 p-10 text-center text-sm text-zinc-400">
              No projects yet. Create one above to open the studio.
            </div>
          ) : (
            <ul className="grid gap-3 sm:grid-cols-2" aria-label="Your projects">
              {state.projects.map((p) => (
                <li key={p.id} className="flex flex-col gap-3 rounded-xl border border-zinc-800 bg-zinc-900/70 p-4" data-testid="project-card">
                  {renaming?.id === p.id ? (
                    <form onSubmit={onRename} className="flex gap-2">
                      <label htmlFor={`rename-${p.id}`} className="sr-only">
                        New name for {p.name}
                      </label>
                      <input
                        id={`rename-${p.id}`}
                        className={inputClass}
                        value={renaming.name}
                        maxLength={NAME_MAX}
                        ref={(el) => el?.focus()}
                        onChange={(e) => setRenaming({ id: p.id, name: e.target.value })}
                        onKeyDown={(e) => e.key === 'Escape' && setRenaming(null)}
                      />
                      <Button type="submit" size="sm" variant="primary">
                        Save
                      </Button>
                    </form>
                  ) : (
                    <div>
                      <h2 className="truncate text-base font-semibold">
                        <a
                          href={`#/project/${p.id}/music`}
                          className="hover:text-accent-300 hover:underline"
                        >
                          {p.name}
                        </a>
                      </h2>
                      <p className="mt-0.5 text-xs text-zinc-400">
                        {p.genre ? `${GENRES[p.genre].label} · ` : ''}{p.bpm} BPM · {p.sectionCount} section{p.sectionCount === 1 ? '' : 's'} · edited <time dateTime={p.updatedAt}>{dateFormat.format(new Date(p.updatedAt))}</time>
                      </p>
                      {p.description && <p className="mt-1 line-clamp-2 text-sm text-zinc-300">{p.description}</p>}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="primary" onClick={() => navigate({ name: 'project', projectId: p.id, tab: 'music' })}>
                      Open<span className="sr-only"> {p.name}</span>
                    </Button>
                    <Button size="sm" onClick={() => setRenaming({ id: p.id, name: p.name })} disabled={busy}>
                      Rename<span className="sr-only"> {p.name}</span>
                    </Button>
                    <Button size="sm" onClick={() => void run(async () => void (await repo.duplicate(p.id)), 'Duplicate failed.')} disabled={busy}>
                      Duplicate<span className="sr-only"> {p.name}</span>
                    </Button>
                    <Button size="sm" onClick={() => void onExport(p.id)} disabled={busy}>
                      Export<span className="sr-only"> {p.name}</span>
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-300" onClick={() => setConfirmDelete(p)} disabled={busy}>
                      Delete<span className="sr-only"> {p.name}</span>
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <ConfirmDialog
        open={confirmDiscard && unsaved !== null}
        title="Discard unsaved changes?"
        body={<>The changes to “{unsaved?.project.name}” that could not be saved will be lost. Download a backup first if you might need them.</>}
        confirmLabel="Discard changes"
        danger
        onCancel={() => setConfirmDiscard(false)}
        onConfirm={() => {
          setConfirmDiscard(false);
          recovery.set(null);
        }}
      />

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete project?"
        body={
          <>
            “{confirmDelete?.name}” and its local assets will be permanently removed from this browser. Export it first if you
            want a backup.
          </>
        }
        confirmLabel="Delete project"
        danger
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          const target = confirmDelete;
          setConfirmDelete(null);
          if (target)
            void run(async () => {
              await whileNotOpenElsewhere(target.id, () => repo.delete(target.id));
            }, 'Delete failed.');
        }}
      />
    </div>
  );
}
